import axios, { AxiosResponse } from "axios";

import { API_BASE_URL, SessionKeys } from "../utils/constants";
import { getSessionItem, removeSessionItem } from "./function.helper";
import { ApiResponse } from "../types";
import enTranslations from "../i18n/en";
import teTranslations from "../i18n/te";

interface FormDataType {
  token?: string;
  getAll?: (key: string) => string[];
  [key: string]: any;
}

const isPublicProfileCreationRoute = () => {
  if (typeof window === "undefined") return false;

  return /^\/profile\/[^/]+(?:\/basic\/[^/]+)?\/?$/.test(
    window.location.pathname,
  );
};

const isAdminRoute = () => {
  if (typeof window === "undefined") return false;

  return window.location.pathname.startsWith("/admin");
};

// Function to get active language
async function getActiveLang(): Promise<string> {
  const activeLang = await getSessionItem("activeLang");
  return activeLang || "en";
}

async function localizeErrorMessage(message: string): Promise<string> {
  const key = "errors.duplicateProfileMobile";
  if (message !== enTranslations[key]) return message;

  const language = await getActiveLang();
  return language === "te" ? teTranslations[key] : enTranslations[key];
}

// Function to get Authorization token from session storage
async function getAuthToken(): Promise<string | null> {
  return await getSessionItem("sessionToken");
}

// Centralized header configuration
// Centralized header configuration
async function setHeaders(apiUrl = "") {
  const token = isPublicProfileCreationRoute() ? null : await getAuthToken();
  const headers = new axios.AxiosHeaders();

  // ✅ Add Auth if exists
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const profileMatch = apiUrl.match(/^\/(?:bureau|user)\/profile\/([^/?]+)(?:\?|$)/);
  if (profileMatch) {
    const draftToken = sessionStorage.getItem(`profile-registration:${decodeURIComponent(profileMatch[1])}`);
    if (draftToken) headers.set("X-Profile-Edit-Token", draftToken);
  }

  // ✅ Add header to skip ngrok warning page
  headers["ngrok-skip-browser-warning"] = "true";

  // ✅ Optional: custom User-Agent (also works)
  // headers["User-Agent"] = "matrimony-studio-client";

  return headers;
}

// Post Request with FormData
async function postWithFormData(
  apiUrl: string,
  formData: FormData
): Promise<ApiResponse> {
  const headers = await setHeaders(apiUrl);
  headers.set("Content-Type", "multipart/form-data");

  // console.info(`${API_BASE_URL}${apiUrl}`, formData);
  try {
    const response = await axios.post(`${API_BASE_URL}${apiUrl}`, formData, {
      headers,
    });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
}

// Standard Post Request
async function postRequest(
  apiUrl: string,
  formData: FormDataType
): Promise<ApiResponse> {
  const headers = await setHeaders(apiUrl);
  headers.set("Content-Type", "application/json");
  // console.info(`${API_BASE_URL}${apiUrl}`, formData, headers);
  try {
    const response = await axios.post(`${API_BASE_URL}${apiUrl}`, formData, {
      headers,
    });
    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
}

// Get Request
async function getRequest(apiUrl: string): Promise<ApiResponse> {
  const headers = await setHeaders(apiUrl);
  // console.info(`${API_BASE_URL}${apiUrl}`, formData, headers);
  try {
    const reponse = await axios.get(`${API_BASE_URL}${apiUrl}`, {
      headers,
    });
    return handleResponse(reponse);
  } catch (error) {
    return handleError(error);
  }
}

// Put Request with FormData
async function putWithFormData(
  apiUrl: string,
  formData: FormData
): Promise<ApiResponse> {
  const headers = await setHeaders(apiUrl);
  headers.set("Content-Type", "multipart/form-data");
  console.info(`${API_BASE_URL}${apiUrl}`, JSON.stringify(formData));
  try {
    const response = await axios.put(`${API_BASE_URL}${apiUrl}`, formData, {
      headers,
    });
    return handleResponse(response); // ✅ Process via unified handler
  } catch (error) {
    return handleError(error); // ✅ Consistent error handling
  }
}

// Standard Put Request
async function putRequest(
  apiUrl: string,
  formData: FormDataType
): Promise<ApiResponse> {
  const headers = await setHeaders(apiUrl);
  headers.set("Content-Type", "application/json");

  // console.info(`${API_BASE_URL}${apiUrl}`, formData);
  try {
    const response = await axios.put(`${API_BASE_URL}${apiUrl}`, formData, {
      headers,
    });
    return handleResponse(response); // ✅ Process via unified handler
  } catch (error) {
    return handleError(error); // ✅ Consistent error handling
  }
}

// Delete Request
async function deleteRequest(
  apiUrl: string,
  formData: FormDataType
): Promise<ApiResponse> {
  const headers = await setHeaders(apiUrl);

  try {
    const response = await axios.delete(`${API_BASE_URL}${apiUrl}`, {
      headers: headers,
      data: formData,
    });
    return handleResponse(response); // ✅ Process via unified handler
  } catch (error) {
    return handleError(error); // ✅ Consistent error handling
  }
}

async function handleResponse<T = any>(
  response: AxiosResponse
): Promise<ApiResponse<T>> {
  try {
    const { data } = response as { data: ApiResponse<T> };
    // Check if data exists and has the expected structure
    if (!data || typeof data !== "object") {
      console.warn("Invalid response structure", data);
      return {
        success: false,
        data: null,
        message: "Invalid response structure",
      };
    }
    const registration = data.data as { profileEditToken?: string; userId?: string; martialId?: string } | null;
    if (data.success && registration?.profileEditToken) {
      for (const id of [registration.userId, registration.martialId]) {
        if (id) sessionStorage.setItem(`profile-registration:${id}`, registration.profileEditToken);
      }
    }
    if (data.success === false) {
      return { ...data, message: await localizeErrorMessage(data.message) };
    }
    return data; // Return the structured data
  } catch (error) {
    return handleError(error); // Delegate to error handler
  }
}

async function handleError<T = any>(error: any): Promise<ApiResponse<T>> {
  let errorMessage = "An error occurred while processing the response";
  if (axios.isAxiosError(error)) {
    if (error.response) {
      const { status, data } = error.response;

      // Check for 403 status and handle logout
      if (status === 403 || status === 401) {
        await logout(isAdminRoute());
        errorMessage = data?.message || `Access forbidden: ${status}`;
      } else {
        errorMessage =
          data?.message || `Request failed with status code ${status}`;
      }
    } else {
      // Handle network errors
      errorMessage = error.message || "Network error occurred";
    }
  } else if (error instanceof Error) {
    // Handle generic errors
    errorMessage = error.message;
  }

  return {
    success: false,
    data: null,
    message: await localizeErrorMessage(errorMessage),
  };
}

const logout = async (redirectToLogin = true) => {
  await removeSessionItem("sessionToken");
  await removeSessionItem("activeLang");

  if (redirectToLogin) {
    window.location.href = "/admin/login";
  }
};

export {
  postRequest,
  getRequest,
  putRequest,
  deleteRequest,
  postWithFormData,
  putWithFormData,
  logout,
};
